import React, { useState } from 'react';
import axios from 'axios';
import './App.css';
import Login from './components/Login';
import SigningInterface from './components/SigningInterface';
import DocumentActions from './components/DocumentActions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// ✅ Global axios interceptors - set up once at module level, always active
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_name');
      window.location.reload(); // Force back to login screen
    }
    return Promise.reject(error);
  }
);

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const formatCurrency = (value) => {
  if (!value || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Format number with commas for display inside inputs (no $ sign)
// When the field is focused, show the raw value so user can type freely
const formatNum = (value, fieldName, focusedField) => {
  if (focusedField === fieldName) return value === '' ? '' : String(value).replace(/,/g, '');
  if (value === '' || value === null || value === undefined) return '';
  const num = parseFloat(String(value).replace(/,/g, ''));
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Strip commas before storing so parseFloat() works correctly
const stripNum = (value) => String(value).replace(/,/g, '');

function App() {
  // ✅ Auth state
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('auth_token'));
  const [authName, setAuthName] = useState(() => localStorage.getItem('auth_name'));

  const handleLogin = (token, name) => {
    setAuthToken(token);
    setAuthName(name);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_name');
    setAuthToken(null);
    setAuthName(null);
  };

  // ✅ If not logged in, show login screen
  if (!authToken) {
    return <Login onLogin={handleLogin} />;
  }

  return <AppMain authName={authName} onLogout={handleLogout} />;
}

// Main app separated so auth check is clean
function AppMain({ authName, onLogout }) {
  const [stockNumber, setStockNumber] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedDocuments, setGeneratedDocuments] = useState(null);

  const [customerType, setCustomerType] = useState('commercial');
  const [customer, setCustomer] = useState({
    companyName: '', address: '', city: '', state: 'MI', zip: '',
    phone: '', email: '', contactName: '', dotNumber: '', driverLicense: '',
    customerTitle: '', dob: ''
  });

  const [nonExemptExtras, setNonExemptExtras] = useState({ bodyStyle: '', odometer: '' });
  const [vehicleDetails, setVehicleDetails] = useState({ bodyStyle: 'TRACTOR', emptyWeight: '16000' });

  const [payment, setPayment] = useState({
    purchaseDate: new Date().toISOString().split('T')[0],
    deliveryDate: new Date().toISOString().split('T')[0],
    purchasePrice: '', docFees: '0.00', titleFee: '15.00',
    cashDeposit: '', balanceDue: '', deliveryMethod: 'Dealer Delivery',
    warranty: '', salesTax: '', warrantyCompany: ''
  });

  const [hasLien, setHasLien] = useState(false);
  const [lien, setLien] = useState({ name: '', address: '', city: '', state: '', zip: '', eltNumber: '', filingDate: '' });

  const [insurance, setInsurance] = useState({ company: '', policyNumber: '' });

  const [isOdometerExempt, setIsOdometerExempt] = useState(true);
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [taxExempt, setTaxExempt] = useState({
    businessName: '', businessAddress: '', businessCity: '', businessState: '',
    businessZip: '', businessPhone: '', customerName: '', reason: 'rolling-stock'
  });

  const [titleType, setTitleType] = useState('standard');
  const [rd108, setRd108] = useState({ remarks: '' });
  const [inTransitState, setInTransitState] = useState('');
  const [miTitleNumber, setMiTitleNumber] = useState('');
  const [savedSession, setSavedSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('5star_session')); } catch { return null; }
  });
  const [sessionRestored, setSessionRestored] = useState(false);

  // Auto-save all form state to localStorage whenever anything changes
  React.useEffect(() => {
    if (!truck && !customer.companyName) return; // don't save empty form
    const session = {
      stockNumber, truck, customer, payment, hasLien, lien, insurance,
      isOdometerExempt, isTaxExempt, taxExempt, titleType, rd108,
      inTransitState, miTitleNumber, nonExemptExtras, customerType, vehicleDetails,
      savedAt: new Date().toLocaleString()
    };
    const timer = setTimeout(() => {
      localStorage.setItem('5star_session', JSON.stringify(session));
    }, 800); // debounce — save 800ms after last change
    return () => clearTimeout(timer);
  }, [stockNumber, truck, customer, payment, hasLien, lien, insurance,
      isOdometerExempt, isTaxExempt, taxExempt, titleType, rd108,
      inTransitState, miTitleNumber, nonExemptExtras, customerType, vehicleDetails]);

  const restoreSession = () => {
    if (!savedSession) return;
    setStockNumber(savedSession.stockNumber || '');
    setTruck(savedSession.truck || null);
    setCustomer(savedSession.customer || { companyName: '', address: '', city: '', state: 'MI', zip: '', phone: '', email: '', contactName: '', dotNumber: '', driverLicense: '', customerTitle: '' });
    setPayment(savedSession.payment || { purchaseDate: new Date().toISOString().split('T')[0], deliveryDate: new Date().toISOString().split('T')[0], purchasePrice: '', docFees: '0.00', titleFee: '15.00', cashDeposit: '', balanceDue: '', deliveryMethod: 'Dealer Delivery', warranty: '', salesTax: '', warrantyCompany: '' });
    setHasLien(savedSession.hasLien || false);
    setLien(savedSession.lien || { name: '', address: '', city: '', state: '', zip: '', eltNumber: '', filingDate: '' });
    setInsurance(savedSession.insurance || { company: '', policyNumber: '' });
    setIsOdometerExempt(savedSession.isOdometerExempt !== false);
    setIsTaxExempt(savedSession.isTaxExempt || false);
    setTaxExempt(savedSession.taxExempt || { businessName: '', businessAddress: '', businessCity: '', businessState: '', businessZip: '', businessPhone: '', customerName: '', reason: 'rolling-stock' });
    setTitleType(savedSession.titleType || 'standard');
    setRd108(savedSession.rd108 || { remarks: '' });
    setInTransitState(savedSession.inTransitState || '');
    setMiTitleNumber(savedSession.miTitleNumber || '');
    setNonExemptExtras(savedSession.nonExemptExtras || { bodyStyle: '', odometer: '' });
    setCustomerType(savedSession.customerType || 'commercial');
    setVehicleDetails(savedSession.vehicleDetails || { bodyStyle: 'TRACTOR', emptyWeight: '16000' });
    setSavedSession(null);
    setSessionRestored(true);
  };

  const clearSession = () => {
    localStorage.removeItem('5star_session');
    setSavedSession(null);
    setSessionRestored(false);
  };

  const startFresh = () => {
    localStorage.removeItem('5star_session');
    window.location.reload(true);
  };

  const lookupTruck = async () => {
    if (!stockNumber) { setError('Please enter a Stock Number'); return; }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/truck/${stockNumber}`);
      setTruck(response.data);
      setPayment(prev => ({ ...prev, purchasePrice: response.data.sellingPrice || '' }));
      setSuccess('Truck found! Fill in customer details below.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to lookup truck');
      setTruck(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    let fee = 15.00;
    if (titleType === 'instant') fee = 20.00;
    else if (titleType === 'in-transit') fee = 10.00;
    else if (hasLien) fee += 1.00;
    setPayment(prev => ({ ...prev, titleFee: fee.toFixed(2) }));
  }, [hasLien, titleType]);

  React.useEffect(() => {
    const price = parseFloat(payment.purchasePrice) || 0;
    const docs = parseFloat(payment.docFees) || 0;
    const title = parseFloat(payment.titleFee) || 0;
    const deposit = parseFloat(payment.cashDeposit) || 0;
    const warranty = parseFloat(payment.warranty) || 0;
    const salesTax = parseFloat(payment.salesTax) || 0;
    setPayment(prev => ({ ...prev, balanceDue: (price + docs + warranty + salesTax + title - deposit).toFixed(2) }));
  }, [payment.purchasePrice, payment.docFees, payment.titleFee, payment.cashDeposit, payment.warranty, payment.salesTax]);

  const validateRequiredFields = () => {
    const errors = [];
    if (!truck) { errors.push('Please lookup a truck by Stock Number'); return errors; }
    if (!customer.companyName?.trim()) errors.push('Company Name is required');
    if (!customer.address?.trim()) errors.push('Address is required');
    if (!customer.city?.trim()) errors.push('City is required');
    if (!customer.state?.trim()) errors.push('State is required');
    if (!customer.zip?.trim()) errors.push('ZIP Code is required');
    if (!customer.contactName?.trim()) errors.push('Contact Name is required');
    if (!customer.phone?.trim()) errors.push('Phone is required');
    if (!customer.driverLicense?.trim()) errors.push("Driver's License / FEIN is required");
    if (customerType === 'personal' && !customer.dob?.trim()) errors.push('Date of Birth is required for personal clients');
    if (!payment.purchaseDate) errors.push('Purchase Date is required');
    if (!payment.deliveryDate) errors.push('Delivery Date is required');
    if (!payment.purchasePrice || parseFloat(payment.purchasePrice) <= 0) errors.push('Purchase Price is required');
    if (hasLien) {
      if (!lien.name?.trim()) errors.push('Lien Holder Name is required');
      if (!lien.address?.trim()) errors.push('Lien Holder Address is required');
      if (!lien.city?.trim()) errors.push('Lien Holder City is required');
      if (!lien.state?.trim()) errors.push('Lien Holder State is required');
      if (!lien.zip?.trim()) errors.push('Lien Holder ZIP is required');
      if (!lien.eltNumber?.trim()) errors.push('ELT Number is required');
      if (!lien.filingDate?.trim()) errors.push('Lien Filing Date is required');
    }
    if (payment.deliveryMethod === 'In-Transit') {
      if (!insurance.company?.trim()) errors.push('Insurance Company is required for In-Transit delivery');
      if (!insurance.policyNumber?.trim()) errors.push('Insurance Policy Number is required for In-Transit delivery');
    }
    if (isTaxExempt) {
      if (!taxExempt.businessName?.trim()) errors.push('Business Name is required for tax exemption');
      if (!taxExempt.businessAddress?.trim()) errors.push('Business Address is required for tax exemption');
      if (!taxExempt.businessCity?.trim()) errors.push('Business City, State, ZIP is required for tax exemption');
      if (!taxExempt.businessPhone?.trim()) errors.push('Business Phone is required for tax exemption');
      if (!taxExempt.customerName?.trim()) errors.push('Customer Contact Name is required for tax exemption');
    }
    if (!isOdometerExempt) {
      if (!nonExemptExtras.odometer?.toString().trim()) errors.push('Odometer Reading is required');
      if (!nonExemptExtras.bodyStyle?.trim()) errors.push('Body Style is required');
      if (!customer.customerTitle?.trim()) errors.push('Customer Title is required');
    }
    return errors;
  };

  const generateDocuments = async () => {
    const validationErrors = validateRequiredFields();
    if (validationErrors.length > 0) {
      setError(<div><strong>Please fill in all required fields:</strong><ul style={{marginTop:'10px',paddingLeft:'20px'}}>{validationErrors.map((err,idx)=><li key={idx}>{err}</li>)}</ul></div>);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.post(`${API_URL}/api/generate-documents`, {
        truck: { ...truck, bodyStyle: !isOdometerExempt ? nonExemptExtras.bodyStyle : '', odometer: !isOdometerExempt ? nonExemptExtras.odometer : '' },
        customer: { ...customer },
        payment,
        lien: hasLien ? lien : null,
        insurance,
        taxExempt: isTaxExempt ? taxExempt : null,
        rd108: { remarks: rd108.remarks || '' },
        isOdometerExempt
      });
      setGeneratedDocuments({ documentPaths: response.data.documentPaths, documentPath: response.data.documentPaths[0], customer, truck });
      setSuccess('Documents generated successfully!');
      if (response.data.folder) window.open(response.data.folder, '_blank');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate documents');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 20px'}}>
        <div>
          <h1>🚚 5 Star Truck Sales</h1>
          <p>Document Management System</p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <span style={{color:'rgba(255,255,255,0.7)',fontSize:'14px'}}>👤 {authName}</span>
          <button
            onClick={onLogout}
            style={{background:'rgba(255,255,255,0.15)',color:'white',border:'1px solid rgba(255,255,255,0.3)',borderRadius:'6px',padding:'8px 16px',cursor:'pointer',fontSize:'13px'}}
          >
            Sign Out
          </button>
        </div>
      </header>

      <main className="App-main">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Session restore banner */}
        {savedSession && !sessionRestored && (
          <div style={{background:'#fff9e6',border:'2px solid #f59e0b',borderRadius:'8px',padding:'16px 20px',marginBottom:'16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'12px'}}>
            <div>
              <strong style={{color:'#92400e'}}>📋 Unsaved session found</strong>
              <div style={{color:'#78350f',fontSize:'13px',marginTop:'4px'}}>
                {savedSession.truck ? `Stock #${savedSession.stockNumber} — ${savedSession.truck.year || ''} ${savedSession.truck.make || ''}` : 'Partial form data'} &nbsp;·&nbsp; Saved {savedSession.savedAt}
              </div>
            </div>
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={restoreSession} className="btn-primary" style={{padding:'8px 20px',fontSize:'14px'}}>Resume</button>
              <button onClick={clearSession} className="btn-secondary" style={{padding:'8px 20px',fontSize:'14px'}}>Discard</button>
            </div>
          </div>
        )}

        {/* Start Fresh button — only show when form has data */}
        {(truck || customer.companyName) && (
          <div style={{textAlign:'right',marginBottom:'8px'}}>
            <button onClick={startFresh} className="btn-secondary" style={{padding:'6px 16px',fontSize:'13px',opacity:0.8}}>🗑 Start Fresh</button>
          </div>
        )}

        {/* Step 1: Truck Lookup */}
        <section className="card">
          <h2>Step 1: Lookup Truck</h2>
          <div className="lookup-form">
            <input
              type="text"
              value={stockNumber}
              onChange={(e) => setStockNumber(e.target.value.toUpperCase())}
              placeholder="Enter Stock Number (e.g., LX9482)"
              onKeyPress={(e) => e.key === 'Enter' && lookupTruck()}
            />
            <button onClick={lookupTruck} disabled={loading || !stockNumber} className="btn-primary">
              {loading ? 'Looking up...' : 'Lookup Truck'}
            </button>
          </div>
          {truck && (
            <div className="truck-info">
              <h3>✓ Truck Found</h3>
              <div className="info-grid">
                <div><strong>Stock #:</strong> {truck.stockNumber}</div>
                <div><strong>Year:</strong> {truck.year}</div>
                <div><strong>Make:</strong> {truck.make}</div>
                <div><strong>Model:</strong> {truck.model}</div>
                <div><strong>VIN:</strong> {truck.vin}</div>
                <div><strong>Price:</strong> {formatCurrency(truck.sellingPrice)}</div>
              </div>
            </div>
          )}
        </section>

        {truck && (
          <>
            {/* Step 2: Customer Information */}
            <section className="card">
              <h3>Step 2: Customer Information</h3>

              {/* Personal / Commercial toggle */}
              <div style={{display:'flex',gap:'0',marginBottom:'16px',borderRadius:'8px',overflow:'hidden',border:'2px solid #667eea',width:'fit-content'}}>
                <button type="button"
                  onClick={()=>setCustomerType('commercial')}
                  style={{padding:'10px 24px',fontSize:'15px',fontWeight:'600',border:'none',cursor:'pointer',background:customerType==='commercial'?'linear-gradient(135deg,#667eea,#764ba2)':'white',color:customerType==='commercial'?'white':'#667eea',transition:'all 0.2s'}}>
                  🏢 Commercial
                </button>
                <button type="button"
                  onClick={()=>setCustomerType('personal')}
                  style={{padding:'10px 24px',fontSize:'15px',fontWeight:'600',border:'none',cursor:'pointer',background:customerType==='personal'?'linear-gradient(135deg,#667eea,#764ba2)':'white',color:customerType==='personal'?'white':'#667eea',transition:'all 0.2s'}}>
                  👤 Personal
                </button>
              </div>

              {customerType === 'commercial' && (
                <a href="https://www.e-secretaryofstate.com" target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{display:'inline-block',marginBottom:'16px',textDecoration:'none'}}>🔍 Entity Search</a>
              )}

              <div className="form-grid">
                {customerType === 'commercial' ? (
                  <label>Company Name <span style={{color:'red'}}>*</span><input type="text" value={customer.companyName} onChange={(e)=>setCustomer({...customer,companyName:e.target.value})} /></label>
                ) : (
                  <label>Full Name <span style={{color:'red'}}>*</span><input type="text" value={customer.companyName} onChange={(e)=>setCustomer({...customer,companyName:e.target.value})} placeholder="First and Last Name" /></label>
                )}
                <label>Contact Name <span style={{color:'red'}}>*</span><input type="text" value={customer.contactName} onChange={(e)=>setCustomer({...customer,contactName:e.target.value})} /></label>
                <label>Address <span style={{color:'red'}}>*</span><input type="text" value={customer.address} onChange={(e)=>setCustomer({...customer,address:e.target.value})} /></label>
                <label>City <span style={{color:'red'}}>*</span><input type="text" value={customer.city} onChange={(e)=>setCustomer({...customer,city:e.target.value})} /></label>
                <label>State <span style={{color:'red'}}>*</span>
                  <select value={customer.state} onChange={(e)=>setCustomer({...customer,state:e.target.value})}>
                    <option value="">Select...</option>
                    {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>ZIP Code <span style={{color:'red'}}>*</span><input type="text" value={customer.zip} onChange={(e)=>setCustomer({...customer,zip:e.target.value})} /></label>
                <label>Phone <span style={{color:'red'}}>*</span><input type="tel" value={customer.phone} onChange={(e)=>setCustomer({...customer,phone:formatPhoneNumber(e.target.value)})} placeholder="(555) 123-4567" maxLength="14" /></label>
                <label>Email<input type="email" value={customer.email} onChange={(e)=>setCustomer({...customer,email:e.target.value})} /></label>

                {customerType === 'commercial' && (
                  <label>
                    DOT Number
                    <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                      <input type="text" value={customer.dotNumber} onChange={(e)=>setCustomer({...customer,dotNumber:e.target.value})} placeholder="Enter DOT Number" style={{flex:1}} />
                      <button type="button" onClick={()=>{ if(customer.dotNumber){ window.open(`https://ai.fmcsa.dot.gov/SMS/Carrier/${customer.dotNumber}/Overview.aspx?FirstView=True`,'_blank'); } else { alert('Please enter a DOT Number first'); }}} className="btn-secondary" style={{padding:'8px 16px',fontSize:'14px',whiteSpace:'nowrap',minWidth:'auto'}}>🔍 Lookup</button>
                    </div>
                  </label>
                )}

                {customerType === 'personal' && (
                  <label>
                    DOT Number <span style={{fontSize:'12px',color:'#888'}}>(optional)</span>
                    <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                      <input type="text" value={customer.dotNumber} onChange={(e)=>setCustomer({...customer,dotNumber:e.target.value})} placeholder="Enter DOT Number if applicable" style={{flex:1}} />
                      <button type="button" onClick={()=>{ if(customer.dotNumber){ window.open(`https://ai.fmcsa.dot.gov/SMS/Carrier/${customer.dotNumber}/Overview.aspx?FirstView=True`,'_blank'); } else { alert('Please enter a DOT Number first'); }}} className="btn-secondary" style={{padding:'8px 16px',fontSize:'14px',whiteSpace:'nowrap',minWidth:'auto'}}>🔍 Lookup</button>
                    </div>
                    <small style={{display:'block',marginTop:'4px',color:'#666'}}>Required if claiming Rolling Stock tax exemption</small>
                  </label>
                )}

                {customerType === 'commercial' ? (
                  <label>
                    Driver's License / FEIN <span style={{color:'red'}}>*</span>
                    <input type="text" value={customer.driverLicense} onChange={(e)=>setCustomer({...customer,driverLicense:e.target.value})} placeholder="Driver's License or FEIN" />
                  </label>
                ) : (
                  <label>
                    Driver's License <span style={{color:'red'}}>*</span>
                    <input type="text" value={customer.driverLicense} onChange={(e)=>setCustomer({...customer,driverLicense:e.target.value})} />
                    <small style={{display:'block',marginTop:'4px',color:'#666'}}>Appears in DLN line 1 on RD-108</small>
                  </label>
                )}

                {customerType === 'personal' && (
                  <>
                    <label>
                      Date of Birth <span style={{color:'red'}}>*</span>
                      <input type="date" value={customer.dob||''} onChange={(e)=>setCustomer({...customer,dob:e.target.value})} />
                      <small style={{display:'block',marginTop:'4px',color:'#666'}}>Appears in DLN line 3 on RD-108</small>
                    </label>
                    {customer.state && customer.state !== 'MI' && (
                      <div style={{background:'#f0f9ff',border:'2px solid #0ea5e9',borderRadius:'6px',padding:'12px',gridColumn:'1/-1'}}>
                        <strong style={{color:'#0c4a6e',fontSize:'13px'}}>📋 Out-of-State detected — RD-108 line 2 will show: </strong>
                        <code style={{color:'#075985',fontSize:'13px'}}>OoS-{customer.state}</code>
                      </div>
                    )}
                  </>
                )}

                {!isOdometerExempt && (
                  <label>
                    Customer Title <span style={{color:'red'}}>*</span>
                    <input type="text" value={customer.customerTitle} onChange={(e)=>setCustomer({...customer,customerTitle:e.target.value})} placeholder="e.g. President, Member, Owner" />
                    <small style={{display:'block',marginTop:'4px',color:'#666'}}>Customer's role within their company — appears on Odometer Statement</small>
                  </label>
                )}
              </div>
            </section>

            {/* Step 3: Payment Details */}
            <section className="card">
              <h3>Step 3: Payment Details</h3>
              <div className="form-grid">
                <label>Purchase Date <span style={{color:'red'}}>*</span><input type="date" value={payment.purchaseDate} onChange={(e)=>setPayment({...payment,purchaseDate:e.target.value})} /></label>
                <label>Delivery Date <span style={{color:'red'}}>*</span><input type="date" value={payment.deliveryDate} onChange={(e)=>setPayment({...payment,deliveryDate:e.target.value})} /></label>
                <label>Purchase Price <span style={{color:'red'}}>*</span><input type="text" inputMode="decimal" value={formatNum(payment.purchasePrice,'purchasePrice',focusedField)} onFocus={()=>setFocusedField('purchasePrice')} onBlur={()=>setFocusedField(null)} onChange={(e)=>setPayment({...payment,purchasePrice:stripNum(e.target.value)})} /></label>
                <label>Doc Fees<input type="text" inputMode="decimal" value={formatNum(payment.docFees,'docFees',focusedField)} onFocus={()=>setFocusedField('docFees')} onBlur={()=>setFocusedField(null)} onChange={(e)=>setPayment({...payment,docFees:stripNum(e.target.value)})} /></label>
                <label>Title Type<select value={titleType} onChange={(e)=>setTitleType(e.target.value)}><option value="standard">Standard Title ($15)</option><option value="instant">Instant Title ($20)</option><option value="in-transit">In-Transit Permit ($10)</option></select></label>
                <label>
                  Odometer Disclosure
                  <select value={isOdometerExempt ? 'exempt' : 'non-exempt'} onChange={(e) => setIsOdometerExempt(e.target.value === 'exempt')}>
                    <option value="exempt">Exempt (Heavy Truck ≥ 16,001 lbs)</option>
                    <option value="non-exempt">Non-Exempt (Requires Odometer Statement)</option>
                  </select>
                  <small style={{display:'block',marginTop:'4px',color:'#666'}}>Heavy trucks over 16,000 lbs GVWR are odometer exempt under federal law</small>
                </label>
                <label>
                  Body Style
                  <input type="text" value={vehicleDetails.bodyStyle} onChange={(e)=>setVehicleDetails({...vehicleDetails,bodyStyle:e.target.value})} />
                  <small style={{display:'block',marginTop:'4px',color:'#666'}}>Appears on RD-108 — default is TRACTOR</small>
                </label>
                <label>
                  Base MSRP / Empty Weight
                  <input type="text" inputMode="decimal" value={vehicleDetails.emptyWeight} onChange={(e)=>setVehicleDetails({...vehicleDetails,emptyWeight:e.target.value})} />
                  <small style={{display:'block',marginTop:'4px',color:'#666'}}>Appears on RD-108 — default is 16,000</small>
                </label>
                <label>Title Fee<input type="number" step="0.01" value={payment.titleFee} readOnly className="readonly" /></label>
                <label>Cash Deposit<input type="text" inputMode="decimal" value={formatNum(payment.cashDeposit,'cashDeposit',focusedField)} onFocus={()=>setFocusedField('cashDeposit')} onBlur={()=>setFocusedField(null)} onChange={(e)=>setPayment({...payment,cashDeposit:stripNum(e.target.value)})} /></label>
                <label>Warranty Company <span style={{fontSize:'12px',color:'#888'}}>(optional)</span>
                <select value={payment.warrantyCompany} onChange={(e)=>setPayment({...payment,warrantyCompany:e.target.value,warranty:e.target.value?payment.warranty:''})}>
                  <option value="">-- None --</option>
                  <option value="Truck Master">Truck Master</option>
                  <option value="Premium 2000">Premium 2000</option>
                </select></label>
                {payment.warrantyCompany && <label>Warranty Amount<input type="text" inputMode="decimal" value={formatNum(payment.warranty,'warranty',focusedField)} onFocus={()=>setFocusedField('warranty')} onBlur={()=>setFocusedField(null)} placeholder="0.00" onChange={(e)=>setPayment({...payment,warranty:stripNum(e.target.value)})} /></label>}
                <label>Sales Tax <span style={{fontSize:'12px',color:'#888'}}>(optional)</span><input type="text" inputMode="decimal" value={formatNum(payment.salesTax,'salesTax',focusedField)} onFocus={()=>setFocusedField('salesTax')} onBlur={()=>setFocusedField(null)} placeholder="0.00" onChange={(e)=>setPayment({...payment,salesTax:stripNum(e.target.value)})} /></label>
                <label>Balance Due<input type="text" value={formatNum(payment.balanceDue,'balanceDue',focusedField)} readOnly className="readonly" /></label>

                {/* Payment Breakdown */}
                <div className="full-width" style={{background:'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',color:'white',padding:'20px',borderRadius:'8px',marginTop:'20px',boxShadow:'0 4px 6px rgba(0,0,0,0.1)'}}>
                  <h4 style={{margin:'0 0 15px 0',fontSize:'18px',fontWeight:'600'}}>💰 Payment Breakdown</h4>
                  <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:'8px',fontSize:'16px'}}>
                    <div>Purchase Price:</div><div style={{textAlign:'right',fontWeight:'600'}}>{formatCurrency(payment.purchasePrice)}</div>
                    <div>Doc Fees:</div><div style={{textAlign:'right',fontWeight:'600'}}>{formatCurrency(payment.docFees)}</div>
                    <div>Title Fee:</div><div style={{textAlign:'right',fontWeight:'600'}}>{formatCurrency(payment.titleFee)}</div>
                    <div style={{borderTop:'2px solid rgba(255,255,255,0.3)',paddingTop:'8px',marginTop:'4px'}}>Subtotal:</div>
                    <div style={{borderTop:'2px solid rgba(255,255,255,0.3)',paddingTop:'8px',marginTop:'4px',textAlign:'right',fontWeight:'600'}}>{formatCurrency((parseFloat(payment.purchasePrice)||0)+(parseFloat(payment.docFees)||0)+(parseFloat(payment.titleFee)||0))}</div>
                    <div>Cash Deposit:</div><div style={{textAlign:'right',fontWeight:'600'}}>- {formatCurrency(payment.cashDeposit)}</div>
                    <div style={{borderTop:'3px solid white',paddingTop:'12px',marginTop:'8px',fontSize:'20px',fontWeight:'700'}}>Balance Due:</div>
                    <div style={{borderTop:'3px solid white',paddingTop:'12px',marginTop:'8px',textAlign:'right',fontSize:'24px',fontWeight:'700',color:'#ffd700'}}>{formatCurrency(payment.balanceDue)}</div>
                  </div>
                </div>

                {/* Delivery Method */}
                <label className="full-width">
                  Delivery Method
                  <select value={payment.deliveryMethod||'Dealer Delivery'} onChange={(e)=>{setPayment({...payment,deliveryMethod:e.target.value});if(e.target.value==='Dealer Delivery'){setInTransitState('');setMiTitleNumber('');}}}>
                    <option value="Dealer Delivery">Dealer Delivery</option>
                    <option value="In-Transit">In-Transit</option>
                  </select>
                </label>

                {payment.deliveryMethod === 'In-Transit' && (
                  <>
                    <label>MI Title Number<input type="text" value={miTitleNumber} onChange={(e)=>setMiTitleNumber(e.target.value)} placeholder="MI00xxxxxxx" /><small style={{display:'block',marginTop:'5px',color:'#666'}}>Enter the Michigan title number for in-transit titles</small></label>
                    <label>
                      State to be Titled In
                      <select value={inTransitState} onChange={(e)=>setInTransitState(e.target.value)}>
                        <option value="">Select State...</option>
                        {["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </label>
                    {(miTitleNumber||inTransitState)&&(
                      <div className="full-width" style={{background:'#f0f9ff',border:'2px solid #0ea5e9',borderRadius:'6px',padding:'15px',marginTop:'10px'}}>
                        <strong style={{color:'#0c4a6e',display:'block',marginBottom:'8px'}}>📝 Preview - Will appear in RD-108 Remarks:</strong>
                        <p style={{fontFamily:'monospace',fontSize:'13px',margin:0,color:'#075985',lineHeight:'1.6'}}>IN-TRANSIT- MI Title# {miTitleNumber||'MI00xxxxxxx'} to be titled in State of {inTransitState||'xxxx'}</p>
                      </div>
                    )}
                  </>
                )}

                {/* RD-108 Remarks */}
                <label className="full-width">
                  RD-108 Remarks (Exactly as it will appear on form)
                  <div style={{display:'flex',gap:'10px',marginTop:'5px',marginBottom:'5px'}}>
                    <button type="button" className="btn-primary" onClick={()=>{let t=`Tax Exempt: ROLLING STOCK\nDOT# ${customer.dotNumber||''}\nVehicle sold AS-IS, where-is. No warranties expressed or implied.`;if(payment.deliveryMethod==='In-Transit'&&(miTitleNumber||inTransitState)){t+=`\nIN-TRANSIT- MI Title# ${miTitleNumber||'MI00xxxxxxx'} to be titled in State of ${inTransitState||'xxxx'}`;}t+=`\nDelivery Method: ${payment.deliveryMethod||'Dealer Delivery'}`;setRd108({...rd108,remarks:t});}}>Load Default Template</button>
                    <button type="button" className="btn-secondary" onClick={()=>setRd108({...rd108,remarks:''})}>Clear</button>
                  </div>
                  <textarea value={rd108.remarks||''} onChange={(e)=>setRd108({...rd108,remarks:e.target.value})} placeholder="Leave blank to use default template, or type your custom remarks here..." rows="6" style={{fontFamily:'monospace',fontSize:'13px',lineHeight:'1.5'}} />
                  <small style={{display:'block',marginTop:'5px',color:'#666'}}><strong>This text appears EXACTLY as typed on the RD-108.</strong> Click "Load Default Template" to start with the standard format, then edit as needed.</small>
                </label>
              </div>
            </section>

            {/* Step 4: Lienholder */}
            <section className="card">
              <h3>Step 4: Lienholder Information (Optional)</h3>
              <label className="checkbox-label"><input type="checkbox" checked={hasLien} onChange={(e)=>setHasLien(e.target.checked)} />This sale involves a lienholder</label>
              {hasLien && (
                <div className="form-grid" style={{marginTop:'15px'}}>
                  <label>Lien Holder Name <span style={{color:'red'}}>*</span><input type="text" value={lien.name} onChange={(e)=>setLien({...lien,name:e.target.value})} /></label>
                  <label>Address <span style={{color:'red'}}>*</span><input type="text" value={lien.address} onChange={(e)=>setLien({...lien,address:e.target.value})} /></label>
                  <label>City <span style={{color:'red'}}>*</span><input type="text" value={lien.city} onChange={(e)=>setLien({...lien,city:e.target.value})} /></label>
                  <label>State <span style={{color:'red'}}>*</span><input type="text" value={lien.state} onChange={(e)=>setLien({...lien,state:e.target.value})} maxLength="2" /></label>
                  <label>ZIP <span style={{color:'red'}}>*</span><input type="text" value={lien.zip} onChange={(e)=>setLien({...lien,zip:e.target.value})} /></label>
                  <label>ELT Number <span style={{color:'red'}}>*</span><input type="text" value={lien.eltNumber} onChange={(e)=>setLien({...lien,eltNumber:e.target.value})} /></label>
                  <label>Lien Filing Date <span style={{color:'red'}}>*</span><input type="date" value={lien.filingDate} onChange={(e)=>setLien({...lien,filingDate:e.target.value})} /><small style={{display:'block',marginTop:'5px',color:'#666'}}>Appears on RD-108 above Lien Code ID</small></label>
                </div>
              )}
            </section>

            {/* Step 5: Insurance */}
            <section className="card">
              <h3>Step 5: Insurance Information</h3>
              {payment.deliveryMethod === 'In-Transit' && (
                <p style={{color:'#c0392b',fontSize:'14px',marginBottom:'10px',fontWeight:'600'}}>⚠️ Insurance is required for In-Transit deliveries.</p>
              )}
              <div className="form-grid">
                <label>Insurance Company {payment.deliveryMethod==='In-Transit'&&<span style={{color:'red'}}>*</span>}<input type="text" value={insurance.company} onChange={(e)=>setInsurance({...insurance,company:e.target.value})} /></label>
                <label>Policy Number {payment.deliveryMethod==='In-Transit'&&<span style={{color:'red'}}>*</span>}<input type="text" value={insurance.policyNumber} onChange={(e)=>setInsurance({...insurance,policyNumber:e.target.value})} /></label>
              </div>
            </section>

            {/* Step 6: Tax Exemption */}
            <section className="card">
              <h3>Step 6: Tax Exemption Information (Optional)</h3>
              <label className="checkbox-label"><input type="checkbox" checked={isTaxExempt} onChange={(e)=>setIsTaxExempt(e.target.checked)} />This sale qualifies for tax exemption (Rolling Stock)</label>
              {isTaxExempt && (
                <div className="form-grid" style={{marginTop:'15px'}}>
                  <label>Business Name <span style={{color:'red'}}>*</span><input type="text" value={taxExempt.businessName} onChange={(e)=>setTaxExempt({...taxExempt,businessName:e.target.value})} placeholder="Leave blank to use customer company name" /></label>
                  <label>Business Address <span style={{color:'red'}}>*</span><input type="text" value={taxExempt.businessAddress} onChange={(e)=>setTaxExempt({...taxExempt,businessAddress:e.target.value})} placeholder="Leave blank to use customer address" /></label>
                  <label>Business City, State, ZIP <span style={{color:'red'}}>*</span><input type="text" value={taxExempt.businessCity} onChange={(e)=>setTaxExempt({...taxExempt,businessCity:e.target.value})} placeholder="Leave blank to use customer city/state/zip" /></label>
                  <label>Business Phone <span style={{color:'red'}}>*</span><input type="tel" value={taxExempt.businessPhone} onChange={(e)=>setTaxExempt({...taxExempt,businessPhone:formatPhoneNumber(e.target.value)})} placeholder="Leave blank to use customer phone" maxLength="14" /></label>
                  <label>Customer Contact Name <span style={{color:'red'}}>*</span><input type="text" value={taxExempt.customerName} onChange={(e)=>setTaxExempt({...taxExempt,customerName:e.target.value})} placeholder="Leave blank to use contact name" /></label>
                </div>
              )}
            </section>

            {/* Step 7: Odometer Statement (NonExempt only) */}
            {!isOdometerExempt && (
              <section className="card">
                <h3>Step 7: Odometer Statement</h3>
                <p style={{color:'#555',fontSize:'14px',marginBottom:'16px'}}>Required for non-exempt sales. Appears on the Michigan Odometer Mileage Statement (BDVR-108).</p>
                <div className="form-grid">
                  <label>
                    Odometer Reading (No Tenths) <span style={{color:'red'}}>*</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={nonExemptExtras.odometer}
                      onChange={(e) => setNonExemptExtras({...nonExemptExtras, odometer: e.target.value})}
                      placeholder="e.g. 485000"
                    />
                    <small style={{display:'block',marginTop:'4px',color:'#666'}}>Enter whole miles only — do not include tenths</small>
                  </label>
                  <label>
                    Body Style <span style={{color:'red'}}>*</span>
                    <input
                      type="text"
                      value={nonExemptExtras.bodyStyle}
                      onChange={(e) => setNonExemptExtras({...nonExemptExtras, bodyStyle: e.target.value})}
                      placeholder="e.g. TRUCK TRACTOR"
                    />
                  </label>
                </div>
              </section>
            )}

            {/* Document Actions */}
            <DocumentActions
              truckData={{ ...truck, bodyStyle: !isOdometerExempt ? nonExemptExtras.bodyStyle : '', odometer: !isOdometerExempt ? nonExemptExtras.odometer : '' }}
              customerData={customer}
              paymentData={payment}
              lienData={hasLien ? lien : null}
              insuranceData={insurance}
              taxExemptData={isTaxExempt ? taxExempt : null}
              rd108Data={rd108}
              isOdometerExempt={isOdometerExempt}
              vehicleDetails={vehicleDetails}
              customerType={customerType}
            />

            {generatedDocuments && (
              <section className="card">
                <SigningInterface
                  customer={generatedDocuments.customer}
                  truck={generatedDocuments.truck}
                  documentPath={generatedDocuments.documentPath}
                  onSigningComplete={(result) => {
                    setSuccess('Documents signed and uploaded to Google Drive!');
                    setGeneratedDocuments(null);
                  }}
                />
              </section>
            )}
          </>
        )}
      </main>

      <footer className="App-footer">
        <p>5 Star Truck Sales © 2026 | 991 - 84th St SW, Byron Center, MI 49315</p>
      </footer>
    </div>
  );
}

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .btn-primary { padding:12px 24px;font-size:16px;font-weight:600;background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);color:white;border:none;border-radius:8px;cursor:pointer;transition:all 0.3s ease;box-shadow:0 4px 6px rgba(0,0,0,0.1); }
  .btn-primary:hover { transform:translateY(-2px);box-shadow:0 6px 12px rgba(0,0,0,0.15); }
  .btn-primary:active { transform:translateY(0); }
  .btn-primary:disabled { opacity:0.6;cursor:not-allowed;transform:none; }
  .btn-secondary { padding:12px 24px;font-size:16px;font-weight:600;background:white;color:#667eea;border:2px solid #667eea;border-radius:8px;cursor:pointer;transition:all 0.3s ease;box-shadow:0 2px 4px rgba(0,0,0,0.1); }
  .btn-secondary:hover { background:#667eea;color:white;transform:translateY(-2px);box-shadow:0 4px 8px rgba(0,0,0,0.15); }
  .btn-secondary:active { transform:translateY(0); }
  .btn-large { padding:16px 32px;font-size:18px;width:100%; }
`;
if (!document.querySelector('style[data-button-styles]')) {
  styleSheet.setAttribute('data-button-styles', 'true');
  document.head.appendChild(styleSheet);
}

export default App;